import { usePhoneStore } from 'v2/state/phone'
import type { REmbedSipCredentials } from 'v2/types/api/auth'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { phoneSDK } from 'v2/utils/phone-sdk'

const SOCKET_WAIT_MS = 10_000
const SOCKET_POLL_MS = 100

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export const waitForSocket = async (
  isConnected: () => boolean,
  timeoutMs = SOCKET_WAIT_MS,
  pollMs = SOCKET_POLL_MS,
): Promise<boolean> => {
  const deadline = Date.now() + timeoutMs
  while (!isConnected()) {
    if (Date.now() >= deadline) {
      return false
    }
    await wait(pollMs)
  }
  return true
}

export const endEmbedTestCall = (): void => {
  if (phoneSDK.isCallActive()) {
    void phoneSDK.hangup()
  }
}

let unbindCallEvents: (() => void) | null = null

// The console's PhoneHandler does both, and the embed does not mount it. Bound for the document,
// not a page, so a call that ends after leaving the builder still clears the phone state.
const bindCallEvents = (): (() => void) => {
  const unbinds = [
    // The run panel's close asks for a hang-up.
    primaryEventBus.on(PrimaryEvents.HANGUP_CALL, () => endEmbedTestCall()),
    primaryEventBus.on(PrimaryEvents.CALL_TERMINATED, () => usePhoneStore.getState().reset()),
  ]
  return () => {
    for (const unbind of unbinds) {
      unbind()
    }
  }
}

// Logs the browser phone in as the session's own user. The embed never registers: a test call
// only needs the socket, and the iframe takes no inbound calls.
export const ensureEmbedPhone = async (
  { username, password }: REmbedSipCredentials,
  audioConstraints?: MediaTrackConstraints,
): Promise<boolean> => {
  if (!phoneSDK.getSdk()) {
    // Plivo STUN only, so a partner's page never sends a STUN request to a third party.
    await phoneSDK.initialize({ username, password }, audioConstraints, true)
    unbindCallEvents ??= bindCallEvents()
  }
  if (!phoneSDK.isConnected()) {
    phoneSDK.connect([])
  }
  return waitForSocket(phoneSDK.isConnected)
}

// The session ended (expiry, takeover or removal): end any test call and log the endpoint out.
export const teardownEmbedPhone = async (): Promise<void> => {
  unbindCallEvents?.()
  unbindCallEvents = null
  if (!phoneSDK.getSdk()) {
    return
  }
  if (phoneSDK.isCallActive()) {
    await phoneSDK.hangup()
  }
  await phoneSDK.destroy()
  // destroy() drops the SDK listeners, so no CALL_TERMINATED clears this.
  usePhoneStore.getState().reset(true)
}
